import _ from 'lodash';
import moment from 'moment';

import { DesignationEntryState } from '../state/designations/state';

import {
  DesignationsGridColumnName,
  DesignationsGridExcelColumns,
  DesignationStatus
} from '../grid/types/column.types';
import { MuniV3Schema, pickDates } from '../services/deal.service.types';
import * as DealModels from '../types/deal.types';
import {
  BasicDesignationIssue,
  DesignationBreakdown,
  DesignationDealStatus,
  DesignationIssue,
  DesignationRating,
  DesignationsCardTypes,
  DesignationSeries
} from '../types/designation.types';
import { ExportableDealDetailsTabs } from '../types/export-modal.types';

import { formatNumber } from '@angular/common';
import {
  ColDef,
  RowNode,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-community';
import { DescriptionCellComponent } from '../grid/components/cells/description.component';
import { ManagerCellComponent } from '../grid/components/cells/manager.component';
import { ColumnSortModel } from '../grid/types/sorting.types';
import { departmentComparator } from '../grid/utils/comparators/department';
import {
  desiDealDateComparator,
  desiDealStatusComparator
} from '../grid/utils/comparators/designations';
import { stringArrayComparator } from '../grid/utils/comparators/string-array';
import {
  TaxStatus,
  taxStatusComparator
} from '../grid/utils/comparators/tax-status';
import { descriptionFormatter } from '../grid/utils/formatters/issue-description';
import { safeNestedFormatter } from '../grid/utils/formatters/safe-nested';
import { stateFormatter } from '../grid/utils/formatters/state';
import { taxStatusFormatter } from '../grid/utils/formatters/tax-status';
import { sortGrid } from '../grid/utils/sort/sort.utils';
import { desiDealDateValueGetter } from '../grid/utils/value-getters/deal-date';
import {
  ratingsValueGetter,
  reduceRatingValues,
  reduceValues
} from '../grid/utils/value-getters/ratings';
import { stateValueGetter } from '../grid/utils/value-getters/state';
import { taxStatusGetter } from '../grid/utils/value-getters/tax-status';
import { ValueGetterParamsDealVersion } from '../grid/utils/value-getters/value-getters.interface';
import { DesignationsCard } from '../state/designations-tab/state';
import { Allotment } from '../types/allotment.types';
import { BasicSeries } from '../types/deal.types';
import { stringComparer } from '../utils/comparers/primitive';
import { DataExport } from '../utils/export/data-export';
import { onlyUnique } from '../utils/filters/only-unique';
import { mapEnums } from '../utils/mapping';
import { isDefined } from '../utils/utils';
import { extractUniqueUnderlyingRatings, getEnhancementLabels, RATING_AGENCIES } from './issue/ratings';

export enum DesiBreakdownUnitTypeCode {
  Amount = 'B',
  Percentage = '%'
}

export type SortType = 0 | 1 | -1;

export function getBreakdownsForAllotment(
  allotmentId: number,
  breakdownData: DesignationBreakdown[]
): DesignationBreakdown[] {
  return (
    breakdownData?.filter(
      (breakdown) => breakdown.allotmentId === allotmentId
    ) || []
  );
}

export function determineDesignationStatus(
  allotmentAmount: number,
  breakdowns: DesignationBreakdown[]
): DesignationStatus {
  if (breakdowns.length > 1) {
    return DesignationStatus.SyndicateEditMultiple;
  }

  if (breakdowns.length === 1) {
    return getSingleBreakdownStatus(allotmentAmount, breakdowns[0]);
  }

  return DesignationStatus.NotSubmitted;
}

function getSingleBreakdownStatus(
  allotmentAmount: number,
  breakdown: DesignationBreakdown
): DesignationStatus {
  if (isNoUnderwriterData(breakdown)) {
    return DesignationStatus.NotSubmitted;
  }

  if (isBSRole(breakdown.lastModifiedByRoleTypeCode)) {
    return allotmentAmount === breakdown.amount
      ? DesignationStatus.Submitted
      : DesignationStatus.Partial;
  }

  return allotmentAmount === breakdown.amount
    ? DesignationStatus.SyndicateEdit
    : DesignationStatus.Partial;
}

export function isBSRole(roleCode: string): boolean {
  return roleCode === 'BS';
}

function isNoUnderwriterData(breakdown: DesignationBreakdown): boolean {
  return breakdown.underwriters.length === 0 || breakdown.amount === 0;
}

export function isBMEnabled(desiEntryData: DesignationEntryState) {
  return (
    desiEntryData.allowBuysideDesiEntry &&
    desiEntryData.areAllotmentsReleased &&
    desiEntryData.isBuysideFirmSetUpInCasa &&
    desiEntryData.isDealerBuysideConnected
  );
}

export function lastModifiedValueGetter(value: string): string {
  if (!isDefined(value)) return '';

  const formattedDate = moment.utc(value).local();

  return formattedDate.isValid()
    ? formattedDate.format('MMM DD YYYY hh:mm A')
    : '';
}

export function isDesignations(activeTab: string) {
  return activeTab === ExportableDealDetailsTabs.designations;
}
export function getIssuesMapper(
  issues: BasicDesignationIssue[]
): DesignationIssue[] {
  return issues.map((issue: DesignationIssue) => {
    return {
      ...issue,
      dealType: mapEnums(
        MuniV3Schema.DealType,
        DealModels.DealType,
        issue.dealType as MuniV3Schema.DealType
      ),
      dealStatus: getDealStatus(issue),
      isDayToDay: issue.dayToDayInd,
      isTBD: issue.toBeDeterminedInd,
      isWeekOf: issue.weekOfInd,
      seniorManager: issue.seniorManagerDepartment,
      saleDate: issue.saleDateTime ? moment.utc(issue.saleDateTime) : null,
      ...pickDates(issue, ['saleDateTime', 'expectedPricingDate']),
      series: issue.series.map((eachSeries: DesignationSeries) => ({
        ...eachSeries,
        isAMT: eachSeries.altMinTaxInd,
        isTaxable: eachSeries.taxableInd,
        underlyingRatings: eachSeries.ratingsUnderlying
          ? eachSeries.ratingsUnderlying
            .map((rating: DesignationRating) =>
              MuniV3Schema.toRating(rating as MuniV3Schema.Rating)
            )
            .filter((rating) => !!rating)
          : []
      }))
    };
  });
}

export function getDealStatus(deal: DesignationIssue): string {
  if (deal.remainingAmountToDesignate === 0) {
    return DesignationDealStatus.Submitted;
  } else if (
    deal.remainingAmountToDesignate > 0 &&
    deal.remainingAmountToDesignate < deal.totalAmountToDesignate
  ) {
    return DesignationDealStatus.Partial;
  } else if (deal.remainingAmountToDesignate === deal.totalAmountToDesignate) {
    return DesignationDealStatus.NotSubmitted;
  }
  return '';
}

export function getCredit(
  breakdownsForAllotment: DesignationBreakdown[],
  allotment: Allotment
) {
  if (
    _.size(breakdownsForAllotment) === 1 &&
    breakdownsForAllotment[0].amount === allotment.amount
  ) {
    return breakdownsForAllotment[0].credit;
  }
  return allotment.credit;
}

export function getBreakdownAmountValue(
  breakdownsForAllotment: DesignationBreakdown[],
  allotment: Allotment
): number {
  const breakdown = _.find(breakdownsForAllotment, (breakdown) => {
    return breakdown.allotmentId === allotment.id;
  });
  return isDefined(breakdown) ? +breakdown.amount : allotment.amount;
}
export const DESIGNATIONS_TAB_CARDS: readonly DesignationsCard[] = [
  {
    key: DesignationsCardTypes.DesignationsActive,
    header: 'Designations Active'
  },
  {
    key: DesignationsCardTypes.DesignationsFinal,
    header: 'Designations Final'
  }
];

export const getIssuesExportData = (
  issues: DesignationIssue[],
  designationStatus: DesignationsCardTypes,
  locale: string
): DataExport.ExportCell[][] => {
  const filteredExportableIssues = [
    getIssuesExportColumnsHeadersByType(designationStatus)
  ];

  issues.forEach((issue) => {
    if (!(issue.isDayToDay || issue.isTBD || issue.isWeekOf)) {
      filteredExportableIssues.push(getIssuesExportDataByTypes(issue, locale));
    }
  });

  return filteredExportableIssues;
};

export const getIssuesExportDataByTypes = (
  issue: DesignationIssue,
  locale: string
): DataExport.ExportCell[] => {
  const date = {
    value: safeNestedFormatter('dealDateFormatted')({
      value: desiDealDateValueGetter(issue)
    })
  };
  const submissionStatus = {
    value: issue[DesignationsGridColumnName.SubmissionStatus]
  };
  const par = {
    value: formatNumber(
      issue[DesignationsGridColumnName.Size] / 1000,
      locale,
      '1.0-0'
    )
  };
  const issueDesc = {
    value: descriptionFormatter({
      data: {
        description: issue[DesignationsGridColumnName.Description]
      }
    })
  };
  const state = {
    value: stateFormatter(true)({
      value: stateValueGetter({ data: issue })
    })
  };
  const formattedRatings = ratingsValueGetter({ data: issue });
  const ratings = RATING_AGENCIES.map((agency) => {
    const underlyingRatings = extractUniqueUnderlyingRatings(
      issue.series as unknown as BasicSeries[],
      agency.name
    );
    const underlyingRatingsValue = reduceValues(underlyingRatings);
    const ratingValue = reduceRatingValues(formattedRatings, agency.key);

    const finalRatingValue = underlyingRatingsValue
      ? `${ratingValue} (${underlyingRatingsValue})`
      : ratingValue;

    return {
      value: finalRatingValue ?? '-'
    };
  });
  const enhancements = issue.series.map((s) => s.insurer).filter(onlyUnique);
  const enhancement = {
    value: getEnhancementLabels(enhancements, true)
  };
  const tax = {
    value: taxStatusFormatter()({
      value: taxStatusGetter(({
        data: issue
      } as unknown) as ValueGetterParamsDealVersion)
    })
  };
  const leadManagerValue = {
    value: safeNestedFormatter('shortName')({
      value: issue[DesignationsGridColumnName.LeadManager]
    })
  };
  const totalAmountToDesignateValue = {
    value: formatNumber(
      issue[DesignationsGridColumnName.TotalAmountToDesignate],
      locale,
      '1.0-2'
    )
  };
  const remainingAmountToDesignate = {
    value: formatNumber(
      issue[DesignationsGridColumnName.RemainingAmountToDesignate],
      locale,
      '1.0-2'
    )
  };
  const totalAmountDesignated = {
    value: formatNumber(
      issue[DesignationsGridColumnName.TotalAmountDesignated],
      locale,
      '1.0-2'
    )
  };
  const lastModied = {
    value: lastModifiedValueGetter(
      issue[DesignationsGridColumnName.LastModified]
    )
  };

  if (issue.designationStatus === DesignationsCardTypes.DesignationsActive) {
    return [
      date,
      submissionStatus,
      par,
      issueDesc,
      state,
      ...ratings,
      enhancement,
      tax,
      leadManagerValue,
      totalAmountToDesignateValue,
      remainingAmountToDesignate,
      lastModied
    ];
  } else {
    return [
      date,
      submissionStatus,
      par,
      issueDesc,
      state,
      ...ratings,
      enhancement,
      tax,
      leadManagerValue,
      totalAmountDesignated,
      lastModied
    ];
  }
};

export const getIssuesExportColumnsHeadersByType = (
  designationStatus: string
): DataExport.ExportCell[] => {
  const ratingsColumns = RATING_AGENCIES.map((agency) => ({
    value: agency.name
  }));

  return [
    { value: DesignationsGridExcelColumns.Date },
    { value: DesignationsGridExcelColumns.SubmissionStatus },
    { value: DesignationsGridExcelColumns.Size },
    { value: DesignationsGridExcelColumns.Description },
    { value: DesignationsGridExcelColumns.State },
    ...ratingsColumns,
    { value: DesignationsGridExcelColumns.Enhancement },
    { value: DesignationsGridExcelColumns.TaxStatus },
    { value: DesignationsGridExcelColumns.LeadManager },
    ...(designationStatus === DesignationsCardTypes.DesignationsActive
      ? [
        { value: DesignationsGridExcelColumns.TotalAmountToDesignate },
        { value: DesignationsGridExcelColumns.RemainingAmountToDesignate }
      ]
      : [{ value: DesignationsGridExcelColumns.TotalAmountDesignated }]),
    { value: DesignationsGridExcelColumns.LastModified }
  ];
};

const getDesignationCommonColumns = (isFinal = false): ColDef[] => [
  {
    headerName: 'Date',
    field: DesignationsGridColumnName.DealDate,
    width: 74,
    minWidth: 74,
    maxWidth: 74,
    sort: 'asc',
    valueGetter: (params: ValueGetterParams) =>
      desiDealDateValueGetter(params.data),
    comparator: (
      _prev: unknown,
      _next: unknown,
      nodeA: RowNode,
      nodeB: RowNode
    ): number => {
      const prevState = desiDealDateValueGetter(nodeA.data);
      const nextState = desiDealDateValueGetter(nodeB.data);

      return desiDealDateComparator(prevState, nextState);
    },
    valueFormatter: safeNestedFormatter('dealDateFormatted'),
    suppressFilter: true,
    suppressMenu: true,
    cellStyle: { padding: 0 }
  },
  {
    headerName: 'Submission Status',
    field: DesignationsGridColumnName.SubmissionStatus,
    maxWidth: 144,
    minWidth: 80,
    comparator: desiDealStatusComparator,
    suppressFilter: true,
    suppressMenu: true,
    cellRenderer: submissionStatusCellRenderer(isFinal)
  },
  {
    headerName: 'Par ($M)',
    field: DesignationsGridColumnName.Size,
    sortingOrder: ['desc', 'asc', null],
    width: 86,
    minWidth: 86,
    maxWidth: 86,
    cellStyle: { textAlign: 'right' },
    valueFormatter: (params: ValueFormatterParams) =>
      formatNumber(params.value / 1000, params.context.getLocale(), '1.0-0'),
    suppressFilter: true,
    suppressMenu: true,
    comparator: (
      _prev: unknown,
      _next: unknown,
      nodeA: RowNode,
      nodeB: RowNode
    ): number =>
      nodeA.data[DesignationsGridColumnName.Size] -
      nodeB.data[DesignationsGridColumnName.Size]
  },
  {
    headerName: 'Issue Description',
    field: DesignationsGridColumnName.Description,
    minWidth: 160,
    suppressSorting: false,
    valueFormatter: descriptionFormatter,
    suppressFilter: true,
    suppressMenu: true,
    cellRenderer: DescriptionCellComponent,
    comparator: (
      _prev: unknown,
      _next: unknown,
      nodeA: RowNode,
      nodeB: RowNode
    ): number => {
      const prevState = descriptionFormatter(nodeA);
      const nextState = descriptionFormatter(nodeB);

      return stringComparer(prevState, nextState);
    }
  },
  {
    headerName: 'State',
    field: DesignationsGridColumnName.State,
    width: 40,
    minWidth: 40,
    maxWidth: 75,
    valueGetter: stateValueGetter,
    valueFormatter: stateFormatter(true),
    comparator: (
      _prev: unknown,
      _next: unknown,
      nodeA: RowNode,
      nodeB: RowNode,
      isInverted: boolean
    ): number => {
      const prevState = stateValueGetter(nodeA);
      const nextState = stateValueGetter(nodeB);

      return stringArrayComparator(
        prevState,
        nextState,
        nodeA,
        nodeB,
        isInverted
      );
    },
    suppressFilter: true,
    suppressMenu: true
  },
  {
    headerName: 'Ratings (M/S/F/K)',
    field: DesignationsGridColumnName.Ratings,
    width: 194,
    minWidth: 194,
    maxWidth: 194,
    cellRenderer: 'ratingsCell',
    valueGetter: ratingsValueGetter,
    suppressSorting: true,
    suppressFilter: true,
    suppressMenu: true
  },
  {
    headerName: 'Tax',
    field: DesignationsGridColumnName.TaxStatus,
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    valueGetter: taxStatusGetter,
    valueFormatter: taxStatusFormatter(),
    comparator: (
      _prev: unknown,
      _next: unknown,
      nodeA: RowNode,
      nodeB: RowNode,
      isInverted: boolean
    ) => {
      const prevState = taxStatusGetter(nodeA) as TaxStatus[];
      const nextState = taxStatusGetter(nodeB) as TaxStatus[];

      return taxStatusComparator(
        prevState,
        nextState,
        nodeA,
        nodeB,
        isInverted
      );
    },
    suppressFilter: true,
    suppressMenu: true
  },
  {
    headerName: 'Lead Manager',
    field: DesignationsGridColumnName.LeadManager,
    maxWidth: 144,
    minWidth: 80,
    cellRenderer: ManagerCellComponent,
    valueFormatter: safeNestedFormatter('name'),
    comparator: departmentComparator,
    keyCreator: safeNestedFormatter('name'),
    suppressFilter: true,
    suppressMenu: true
  }
];

export const getActiveDesignationsGridColumns = (): ColDef[] => {
  const commonColumns = getDesignationCommonColumns();

  return [
    ...commonColumns,
    {
      headerName: 'Total Designation',
      field: DesignationsGridColumnName.TotalAmountToDesignate,
      maxWidth: 144,
      minWidth: 80,
      cellStyle: { textAlign: 'right' },
      suppressSorting: false,
      headerClass: 'wrap-header',
      valueFormatter: (params: ValueFormatterParams) =>
        formatNumber(params.value, 'en-US', '1.0-2'),
      suppressFilter: true,
      suppressMenu: true,
      comparator: (
        _prev: unknown,
        _next: unknown,
        nodeA: RowNode,
        nodeB: RowNode
      ): number =>
        nodeA.data[DesignationsGridColumnName.TotalAmountToDesignate] -
        nodeB.data[DesignationsGridColumnName.TotalAmountToDesignate]
    },
    {
      headerName: 'Remaining Designation',
      field: DesignationsGridColumnName.RemainingAmountToDesignate,
      maxWidth: 164,
      minWidth: 80,
      cellStyle: { textAlign: 'right' },
      suppressSorting: false,
      valueFormatter: (params: ValueFormatterParams) =>
        formatNumber(params.value, 'en-US', '1.0-2'),
      suppressFilter: true,
      suppressMenu: true,
      comparator: (
        _prev: unknown,
        _next: unknown,
        nodeA: RowNode,
        nodeB: RowNode
      ): number =>
        nodeA.data[DesignationsGridColumnName.RemainingAmountToDesignate] -
        nodeB.data[DesignationsGridColumnName.RemainingAmountToDesignate]
    },
    {
      headerName: 'Last Modified',
      field: DesignationsGridColumnName.LastModified,
      maxWidth: 154,
      minWidth: 80,
      suppressSorting: false,
      suppressFilter: true,
      suppressMenu: true,
      valueGetter: (params: ValueGetterParams) =>
        lastModifiedValueGetter(params.data.desiLastModifiedUtcDateTime),
      comparator: (
        _prev: unknown,
        _next: unknown,
        nodeA: RowNode,
        nodeB: RowNode
      ): number => {
        const prevValue = lastModifiedValueGetter(
          nodeA.data[DesignationsGridColumnName.LastModified]
        );
        const nextValue = lastModifiedValueGetter(
          nodeB.data[DesignationsGridColumnName.LastModified]
        );

        return moment(prevValue).isAfter(moment(nextValue)) ? 1 : -1;
      }
    }
  ];
};

export const getFinalDesignationsGridColumns = (): ColDef[] => {
  const commonColumns = getDesignationCommonColumns(true);

  return [
    ...commonColumns,
    {
      headerName: 'Total Amount Designated',
      field: DesignationsGridColumnName.TotalAmountDesignated,
      maxWidth: 288,
      minWidth: 130,
      cellStyle: { textAlign: 'right' },
      suppressSorting: false,
      headerClass: 'wrap-header',
      valueFormatter: (params: ValueFormatterParams) =>
        formatNumber(params.value, 'en-US', '1.0-2'),
      suppressFilter: true,
      suppressMenu: true,
      comparator: (
        _prev: unknown,
        _next: unknown,
        nodeA: RowNode,
        nodeB: RowNode
      ): number =>
        nodeA.data[DesignationsGridColumnName.TotalAmountDesignated] -
        nodeB.data[DesignationsGridColumnName.TotalAmountDesignated]
    },
    {
      headerName: 'Last Modified',
      field: DesignationsGridColumnName.LastModified,
      maxWidth: 154,
      minWidth: 130,
      suppressSorting: false,
      suppressFilter: true,
      suppressMenu: true,
      valueGetter: (params: ValueGetterParams) =>
        lastModifiedValueGetter(params.data.desiLastModifiedUtcDateTime),
      comparator: (
        _prev: unknown,
        _next: unknown,
        nodeA: RowNode,
        nodeB: RowNode
      ): number => {
        const prevValue = lastModifiedValueGetter(
          nodeA.data[DesignationsGridColumnName.LastModified]
        );
        const nextValue = lastModifiedValueGetter(
          nodeB.data[DesignationsGridColumnName.LastModified]
        );

        return moment(prevValue).isAfter(moment(nextValue)) ? 1 : -1;
      }
    }
  ];
};

export const buildColumnsFor = (gridType: DesignationsCardTypes): ColDef[] => {
  switch (gridType) {
    case DesignationsCardTypes.DesignationsActive:
      return getActiveDesignationsGridColumns();
    case DesignationsCardTypes.DesignationsFinal:
      return getFinalDesignationsGridColumns();
    default:
      throw new Error(`Grid type "${gridType}" is not implemented`);
  }
};

const submissionStatusCellRenderer = (isFinal: boolean) => {
  const statusClass = isFinal ? 'designation-status-grey' : '';

  return (params) => {
    let iconHtml = '';
    let status = '';

    if (params.data) {
      if (params.data.remainingAmountToDesignate === 0) {
        iconHtml = `<i class="fa fa-circle fa-lg designation-status-green ${statusClass}" aria-hidden="true"></i>`;
        status = DesignationDealStatus.Submitted;
      } else if (
        params.data.remainingAmountToDesignate > 0 &&
        params.data.remainingAmountToDesignate <
        params.data.totalAmountToDesignate
      ) {
        const halfCircleClass = isFinal ? 'half-circle-final' : 'half-circle';
        iconHtml = `<span class="${halfCircleClass}"></span>`;
        status = DesignationDealStatus.Partial;
      } else if (
        params.data.remainingAmountToDesignate ===
        params.data.totalAmountToDesignate
      ) {
        iconHtml = `<i class="fa fa-circle-o fa-lg designation-status-orange ${statusClass}" aria-hidden="true"></i>`;
        status = DesignationDealStatus.NotSubmitted;
      }
    }

    params.data.dealStatus = status;
    return `<span class="designation-submission-status">
                      ${iconHtml} <span class="desi-submission-text ${statusClass}">${status}</span>
                    </span>`;
  };
};

export const sortIssuesByType = (
  issues: DesignationIssue[],
  sortModel: ColumnSortModel<DesignationsGridColumnName>[],
  issueType: DesignationsCardTypes
) => {
  const gridColumns = buildColumnsFor(issueType);
  const sortMap: ColDef[] = gridColumns.map(
    ({ field, comparator, headerName }: ColDef) => ({
      colId: field,
      comparator,
      headerName
    })
  );
  const sortModelUpdated = sortModel.map((model) => ({
    ...model,
    comparator: sortMap.find(({ colId }) => colId === model.colId)?.comparator
  }));

  return sortGrid(issues, sortModelUpdated);
};

export function getLatestModifiedDate(
  breakdowns: DesignationBreakdown[]
): string | null {
  if (!breakdowns.length) return null;

  const latestTimestamp = Math.max(
    ...breakdowns.map((breakdown) =>
      new Date(breakdown.lastModifiedUtcDateTime).getTime()
    )
  );

  return moment(new Date(latestTimestamp)).utc().format('YYYY-MM-DDTHH:mm:ss');
}
